module Client.Login


open Client
open Client.Services
open Shared
open global.Elmish
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.UseElmish
open Fable.Core.Experimental
open System.Text.RegularExpressions

type ValidEmail = private ValidEmail of string with
  static member Create s =
    if s = "" then
      Error "Required"
    elif not <| Regex.IsMatch(s, "\w+@\w+\.\w+") then
      Error "Enter a valid email address"
    else
      ValidEmail s |> Ok

type ValidPassword = private ValidPassword of string with
  static member Create s =
    if s = "" then Error "Required" else ValidPassword s |> Ok

type LoginState =
  | LoggingIn
  | ResettingPassword

let viewPasswordReset = React.functionComponent (fun (inputProps: {| initialEmail : string; reCaptchaSiteKey : string; resetFinishedCallback : (unit -> unit) |}) ->
  let classes = Styles.useStyles ()
  let (emailAddress, setEmailAddress) = React.useState(inputProps.initialEmail)
  let (validateEmail, setValidateEmail) = React.useState(false)
  let (isBusy, setIsBusy) = React.useState(false)
  let (failureMessage, setFailureMessage) = React.useState<string option>(None)
  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  React.useEffect(effect, [||])
  let handleSubmit (e: Browser.Types.Event) =
    e.preventDefault()
    setValidateEmail(true)
    setFailureMessage(None)

    match ValidEmail.Create emailAddress with
    | Ok (ValidEmail username) ->
      setIsBusy(true)

      let req = { PasswordResetRequest.Username = username; RecaptchaResponse = "" }

      let fn (token: string) =
        let requestWithToken = { req with RecaptchaResponse = token }
        let decoder = Services.decoder<Result<string, string>>

        Fetch.tryPost("/api/PasswordReset", requestWithToken, decoder = decoder)
        |> Promise.iter (fun res ->
          setIsBusy(false)
          match res with
          | Ok (Ok loginResponse) ->
            inputProps.resetFinishedCallback()
          | Ok (Error msg) ->
            setFailureMessage(Some msg)
          | Error (FetchFailed r) when r.Status = 429 ->
            setFailureMessage(Some "Password reset failed")
          | Error _ ->
            setFailureMessage(Some "Password reset failed")
        )

      ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaLoginAction fn
    | _ ->
      setFailureMessage(Some "Please fix validation errors.")

  Mui.container [
    container.component' "main"
    container.maxWidth.xs
    container.children [
      Html.div [
        prop.children [
          Mui.avatar [
            avatar.children [ lockOutlinedIcon [] ]
          ]

          Mui.typography [
            typography.component' "h1"
            typography.variant.h5
            typography.children "Password Reset"
          ]

          Html.form [
            prop.onSubmit handleSubmit
            prop.children [
              Mui.textField [
                textField.value emailAddress
                textField.onChange setEmailAddress
                textField.variant.outlined
                textField.margin.normal
                textField.required true
                textField.fullWidth true
                textField.label "Email Address"
                textField.name "email"
                textField.autoComplete "email"
                //textField.autoFocus true
                prop.onBlur (fun _ -> setValidateEmail(true))

                match validateEmail, ValidEmail.Create emailAddress with
                | true, Error msg -> textField.error true; textField.helperText msg
                | _ -> ()
              ]

              Mui.grid [
                grid.container true
                grid.spacing._2
                grid.direction.column
                grid.children [
                  Mui.grid [
                    grid.item true
                    grid.xs.true'
                    grid.children [
                      Mui.button [
                        prop.type'.submit
                        button.fullWidth true
                        button.variant.contained
                        button.color.primary
                        button.disabled isBusy
                        button.children [
                          if isBusy then
                            Mui.circularProgress [
                              circularProgress.size 20
                              circularProgress.color.inherit'
                              prop.style [ style.marginRight 8 ]
                            ]
                          Html.span "Reset Password"
                        ]
                      ]
                    ]
                  ]
                  Mui.grid [
                    grid.item true
                    grid.xs.true'
                    grid.children [
                      Mui.button [
                        prop.type'.button
                        button.fullWidth true
                        button.variant.contained
                        button.color.default'
                        button.children [
                          Html.span "Back to Login"
                        ]
                        prop.onClick (fun _ ->
                          inputProps.resetFinishedCallback()
                        )
                      ]
                    ]
                  ]
                ]
              ]

              match failureMessage with
              | Some msg ->
                  Mui.typography [
                    typography.color.error
                    typography.children msg
                  ]
              | None -> Html.none
            ]
          ]
        ]
      ]
    ]
  ]
)

let useStyles = Styles.makeStyles(fun styles theme ->
  {|
    paper = styles.create [
      style.marginTop (theme.spacing 8)
      style.display.flex
      style.flexDirection.column
      style.alignItems.center
    ]
    progressBar = styles.create [
      style.margin (theme.spacing 1)
    ]
    avatar = styles.create [
      style.margin (theme.spacing 1)
      style.backgroundColor theme.palette.primary.main
    ]
    form = styles.create [
      style.width (length.perc 100)  // Allegedly fixes an IE 11 issue
      style.marginTop (theme.spacing 1)
    ]
    submit = styles.create [
      style.margin (theme.spacing(3, 0, 2))
    ]
  |}
)

let view = React.functionComponent (fun (inputProps: {| reCaptchaSiteKey : string; parentDispatch : (Shared.Msg -> unit) |}) ->
  let classes = useStyles ()

  let (emailAddress, setEmailAddress) = React.useState("")
  let (validateEmail, setValidateEmail) = React.useState(false)
  let logingState, setLoginState = React.useState(LoginState.LoggingIn)

  let (password, setPassword) = React.useState("")
  let (validatePassword, setValidatePassword) = React.useState(false)

  let (isBusy, setIsBusy) = React.useState(false)

  let (failureMessage, setFailureMessage) = React.useState<string option>(None)

  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  React.useEffect(effect, [||])

  let handleSubmit (e: Browser.Types.Event) =
    e.preventDefault()
    setValidateEmail(true)
    setValidatePassword(true)
    setFailureMessage(None)

    match ValidEmail.Create emailAddress, ValidPassword.Create password with
    | Ok (ValidEmail username), Ok (ValidPassword pwd) ->
      setIsBusy(true)

      let req = { Username = username; Password = pwd; RecaptchaResponse = "" }

      let fn (token: string) =
        let requestWithToken = { req with RecaptchaResponse = token }
        let decoder = Services.decoder<Result<Shared.LoginResponse, string>>

        Fetch.tryPost("/api/Login", requestWithToken, decoder = decoder)
        |> Promise.iter (fun res ->
          setIsBusy(false)
          match res with
          | Ok (Ok loginResponse) ->
            inputProps.parentDispatch (LoginSuccess loginResponse)
          | Ok (Error msg) ->
            setFailureMessage(Some "Login failed")
          | Error (FetchFailed r) when r.Status = 429 ->
            setFailureMessage(Some "Login failed")
          | Error _ ->
            setFailureMessage(Some "Login failed")
        )

      ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaLoginAction fn
    | _ ->
      setFailureMessage(Some "Please fix validation errors.")

  match logingState with
  | ResettingPassword ->
    viewPasswordReset
      {|
        initialEmail = emailAddress
        reCaptchaSiteKey = inputProps.reCaptchaSiteKey; resetFinishedCallback =
          (fun isReset -> setLoginState LoggingIn)
      |}
  | LoggingIn ->
    Mui.container [
      container.component' "main"
      container.maxWidth.xs
      container.children [
        Html.div [
          prop.children [
            Mui.avatar [
              avatar.children [ lockOutlinedIcon [] ]
            ]

            Mui.typography [
              typography.component' "h1"
              typography.variant.h5
              typography.children "Sign in"
            ]

            Html.form [
              prop.onSubmit handleSubmit
              prop.children [
                Mui.textField [
                  textField.value emailAddress
                  textField.onChange setEmailAddress
                  textField.variant.outlined
                  textField.margin.normal
                  textField.required true
                  textField.fullWidth true
                  textField.label "Email Address"
                  textField.name "email"
                  textField.autoComplete "email"
                  //textField.autoFocus true
                  prop.onBlur (fun _ -> setValidateEmail(true))

                  match validateEmail, ValidEmail.Create emailAddress with
                  | true, Error msg -> textField.error true; textField.helperText msg
                  | _ -> ()
                ]

                Mui.textField [
                  textField.value password
                  textField.onChange setPassword
                  textField.variant.outlined
                  textField.margin.normal
                  textField.required true
                  textField.fullWidth true
                  textField.label "Password"
                  textField.name "password"
                  textField.type' "password"
                  textField.autoComplete "current-password"
                  prop.onBlur (fun _ -> setValidatePassword(true))

                  match validatePassword, ValidPassword.Create password with
                  | true, Error msg -> textField.error true; textField.helperText msg
                  | _ -> ()
                ]

                Mui.button [
                  prop.type'.submit
                  button.fullWidth true
                  button.variant.contained
                  button.color.primary
                  button.disabled isBusy
                  button.children [
                    if isBusy then
                      Mui.circularProgress [
                        circularProgress.size 20
                        circularProgress.color.inherit'
                        prop.style [ style.marginRight 8 ]
                      ]
                    Html.span "Sign In"
                  ]
                ]
                Mui.grid [
                  grid.container true
                  grid.children [
                    Mui.grid [
                      grid.item true
                      grid.xs.true'
                      grid.children [
                        Mui.link [
                          prop.href "#"
                          link.variant "body2"
                          link.children "Forgot password?"
                          prop.onClick (fun e ->
                              e.preventDefault ()
                              setLoginState ResettingPassword
                            )
                        ]
                      ]
                    ]
                    // Mui.grid [
                    //   grid.item true
                    //   grid.children [
                    //     Mui.link [
                    //       prop.href "#"
                    //       link.variant "body2"
                    //       link.children "Don't have an account? Sign Up"
                    //       prop.onClick (fun e -> e.preventDefault ())  // to avoid reloading the demo
                    //     ]
                    //   ]
                    // ]
                  ]
                ]

                match failureMessage with
                | Some msg ->
                    Mui.typography [
                      typography.color.error
                      typography.children msg
                    ]
                | None -> Html.none
              ]
            ]
          ]
        ]
      ]
    ]
)